import { registerLocaleData } from "@angular/common";
import { Injectable } from "@angular/core";
import { defer, delay, from, Observable, of } from "rxjs";
import { AppLanguage } from "../enums/app-language.enum";
import { isIn } from "../functions/is-in";
import { joinPromiseRecord } from "../functions/join-promise-record";

/** Languages not necessarily included by default */
type OptionalAppLanguage = Exclude<AppLanguage, "en">;
type LocaleData = Parameters<typeof registerLocaleData>[0];
type ExtraLocaleData = Parameters<typeof registerLocaleData>[2];

@Injectable({
  providedIn: "root",
})
export class AppLocaleService {
  private readonly localeData: Record<OptionalAppLanguage, () => Promise<LocaleData>> = {
    [AppLanguage.Czech]: () => import("@angular/common/locales/cs").then((m) => m.default),
    [AppLanguage.German]: () => import("@angular/common/locales/de").then((m) => m.default),
    [AppLanguage.EnglishUnitedKingdom]: () => import("@angular/common/locales/en-GB").then((m) => m.default),
    [AppLanguage.Spanish]: () => import("@angular/common/locales/es").then((m) => m.default),
    [AppLanguage.French]: () => import("@angular/common/locales/fr").then((m) => m.default),
    [AppLanguage.Italian]: () => import("@angular/common/locales/it").then((m) => m.default),
    [AppLanguage.Dutch]: () => import("@angular/common/locales/nl").then((m) => m.default),
    [AppLanguage.PortuguesePortugal]: () => import("@angular/common/locales/pt-PT").then((m) => m.default),
    [AppLanguage.SerbianLatin]: () => import("@angular/common/locales/sr-Latn").then((m) => m.default),
    [AppLanguage.Turkish]: () => import("@angular/common/locales/tr").then((m) => m.default),
    [AppLanguage.SerbianCyrillic]: () => import("@angular/common/locales/sr-Cyrl").then((m) => m.default),
    [AppLanguage.Korean]: () => import("@angular/common/locales/ko").then((m) => m.default),
    [AppLanguage.Japanese]: () => import("@angular/common/locales/ja").then((m) => m.default),
    [AppLanguage.ChineseSimplified]: () => import("@angular/common/locales/zh-Hans").then((m) => m.default),
    [AppLanguage.ChineseTraditional]: () => import("@angular/common/locales/zh-Hant").then((m) => m.default),
  };

  private readonly extraLocaleData: Record<OptionalAppLanguage, () => Promise<ExtraLocaleData>> = {
    [AppLanguage.Czech]: () => import("@angular/common/locales/extra/cs").then((m) => m.default),
    [AppLanguage.German]: () => import("@angular/common/locales/extra/de").then((m) => m.default),
    [AppLanguage.EnglishUnitedKingdom]: () => import("@angular/common/locales/extra/en-GB").then((m) => m.default),
    [AppLanguage.Spanish]: () => import("@angular/common/locales/extra/es").then((m) => m.default),
    [AppLanguage.French]: () => import("@angular/common/locales/extra/fr").then((m) => m.default),
    [AppLanguage.Italian]: () => import("@angular/common/locales/extra/it").then((m) => m.default),
    [AppLanguage.Dutch]: () => import("@angular/common/locales/extra/nl").then((m) => m.default),
    [AppLanguage.PortuguesePortugal]: () => import("@angular/common/locales/extra/pt-PT").then((m) => m.default),
    [AppLanguage.SerbianLatin]: () => import("@angular/common/locales/extra/sr-Latn").then((m) => m.default),
    [AppLanguage.Turkish]: () => import("@angular/common/locales/extra/tr").then((m) => m.default),
    [AppLanguage.SerbianCyrillic]: () => import("@angular/common/locales/extra/sr-Cyrl").then((m) => m.default),
    [AppLanguage.Korean]: () => import("@angular/common/locales/extra/ko").then((m) => m.default),
    [AppLanguage.Japanese]: () => import("@angular/common/locales/extra/ja").then((m) => m.default),
    [AppLanguage.ChineseSimplified]: () => import("@angular/common/locales/extra/zh-Hans").then((m) => m.default),
    [AppLanguage.ChineseTraditional]: () => import("@angular/common/locales/extra/zh-Hant").then((m) => m.default),
  };

  private readonly importedLocaleDataIds = new Set<string>();

  public loadLocaleData(localeId: string): Observable<void> {
    return defer(() => {
      if (!this.importedLocaleDataIds.has(localeId)) {
        this.importedLocaleDataIds.add(localeId);
        return from(this.register(localeId)).pipe(delay(0));
      }
      return of(<void>undefined);
    });
  }

  private async register(localeId: string): Promise<void> {
    if (isIn(localeId, this.localeData)) {
      const { data, extraData } = await joinPromiseRecord({
        data: this.localeData[localeId](),
        extraData: isIn(localeId, this.extraLocaleData) ? this.extraLocaleData[localeId]() : Promise.resolve(undefined),
      });
      registerLocaleData(data, localeId, extraData);
    }
  }
}
